import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, money, qty } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight, Boxes, TrendingUp, AlertTriangle, Save, Check, Pencil, X, BadgeCheck, FileSpreadsheet, FileText, Layers, Package } from "lucide-react";
import { SortHeader, useSort } from "@/components/SortHeader";
import { toast } from "sonner";

const LIMIT = 40;

const TABS = [
  ["all", "Tüm Stok"],
  ["hammadde", "Hammadde"],
  ["mamul", "Mamül"],
  ["critical", "Kritik Stok"],
  ["margin", "Kâr Marjı"],
];

export default function Stock() {
  const [tab, setTab] = useState("mamul");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pos, setPos] = useState(0);
  const [critical, setCritical] = useState(0);
  const [hammadde, setHammadde] = useState(0);
  const [mamul, setMamul] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [thInput, setThInput] = useState("0");
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [margins, setMargins] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [codeEditId, setCodeEditId] = useState(null);
  const [codeVal, setCodeVal] = useState("");
  const [nameEditId, setNameEditId] = useState(null);
  const [nameVal, setNameVal] = useState("");
  const [exporting, setExporting] = useState("");

  const patchItem = (id, patch) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const saveCode = async (s) => {
    try {
      const d = await api.updateStockCode(s.id, codeVal.trim());
      patchItem(s.id, { code: d.code });
      setCodeEditId(null);
      toast.success("Ürün kodu kaydedildi");
    } catch {
      toast.error("Kod kaydedilemedi");
    }
  };

  const saveName = async (s) => {
    const nm = nameVal.trim();
    if (!nm) { toast.error("Ad boş olamaz"); return; }
    try {
      const d = await api.updateStockMeta(s.id, { name: nm });
      patchItem(s.id, { name: d.name });
      setNameEditId(null);
      toast.success("Ürün adı güncellendi");
    } catch {
      toast.error("Ad kaydedilemedi");
    }
  };

  const toggleCategory = async (s) => {
    const next = s.category === "hammadde" ? "mamul" : "hammadde";
    try {
      const d = await api.updateStockMeta(s.id, { category: next });
      patchItem(s.id, { category: d.category });
      setHammadde((h) => h + (next === "hammadde" ? 1 : -1));
      setMamul((m) => m + (next === "mamul" ? 1 : -1));
      toast.success(`"${(s.name || "").slice(0, 24)}" → ${next === "hammadde" ? "Hammadde" : "Mamül"}`);
      if (tab === "hammadde" || tab === "mamul") setItems((prev) => prev.filter((it) => it.id !== s.id));
    } catch {
      toast.error("Kategori güncellenemedi");
    }
  };

  const doExport = async (format) => {
    setExporting(format);
    try {
      await api.downloadExport(`/export/stock?format=${format}`, `arslan_stok.${format}`);
      toast.success(`Stok listesi ${format.toUpperCase()} olarak indirildi`);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting("");
    }
  };

  const loadAll = async () => {
    const params = { skip: page * LIMIT, limit: LIMIT };
    if (search.trim()) params.search = search.trim();
    if (tab === "hammadde" || tab === "mamul") params.category = tab;
    const d = await api.stock(params);
    setItems(d.items);
    setTotal(d.total);
    setPos(d.positive || 0);
    setCritical(d.critical);
    setHammadde(d.hammadde || 0);
    setMamul(d.mamul || 0);
    setThreshold(d.threshold);
    setThInput(String(d.threshold));
  };
  const loadCritical = async () => {
    const d = await api.stockAlerts();
    setItems(d.items);
    setTotal(d.count);
    setCritical(d.count);
    setThreshold(d.threshold);
    setThInput(String(d.threshold));
  };
  const loadMargins = async () => {
    const d = await api.stockMargins(search.trim() || undefined);
    setMargins(d.items);
    setTotal(d.total);
  };

  useEffect(() => {
    if (tab === "critical") loadCritical();
    else if (tab === "margin") loadMargins();
    else loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, search]);

  const saveThreshold = async () => {
    await api.setThreshold(parseFloat(thInput) || 0);
    toast.success("Kritik stok eşiği güncellendi");
    tab === "critical" ? loadCritical() : loadAll();
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const isCrit = (q) => q <= threshold;
  const paged = tab === "all" || tab === "hammadde" || tab === "mamul";

  const rowStatus = (s) => (s.manual_override ? "Düzeltildi" : s.confirmed ? "Onaylı" : "Bekliyor");
  const stockSort = useSort(items, {
    name: (s) => s.name || "",
    code: (s) => s.code || "",
    category: (s) => s.category || "",
    quantity: (s) => s.quantity ?? 0,
    sell_qty: (s) => s.sell_qty ?? 0,
    sell_amount: (s) => s.sell_amount ?? 0,
    status: rowStatus,
  });
  const sortedItems = stockSort.sorted;
  const marginSort = useSort(margins, {
    name: (m) => m.name || "",
    avg_buy: (m) => m.avg_buy ?? 0,
    avg_sell: (m) => m.avg_sell ?? 0,
    margin: (m) => m.margin ?? 0,
    margin_pct: (m) => m.margin_pct ?? 0,
  });
  const sortedMargins = marginSort.sorted;

  const confirmRow = async (s) => {
    try {
      const d = await api.confirmStock(s.id);
      patchItem(s.id, { confirmed: d.confirmed, confirmed_qty: d.confirmed_qty });
      toast.success(`"${(s.name || "").slice(0, 30)}..." onaylandı`);
    } catch {
      toast.error("Onaylama başarısız");
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditVal(String(s.quantity));
  };

  const saveEdit = async (s) => {
    const q = parseFloat(editVal);
    if (Number.isNaN(q)) {
      toast.error("Geçerli bir miktar girin");
      return;
    }
    try {
      const d = await api.adjustStock(s.id, q);
      patchItem(s.id, { quantity: d.quantity, manual_override: true, confirmed: true, system_qty: d.system_qty });
      setEditId(null);
      toast.success("Stok miktarı güncellendi");
    } catch {
      toast.error("Güncelleme başarısız");
    }
  };

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader title="Stok" subtitle="Hammadde / Mamül ayrımı · ürün adı, kod, kategori ve miktar elle düzenlenebilir" />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Boxes className="h-4 w-4 text-primary" /> Toplam</div>
          <div data-testid="stock-total" className="mt-2 font-mono text-2xl font-semibold tabular">{total.toLocaleString("tr-TR")}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Layers className="h-4 w-4 text-amber-300" /> Hammadde</div>
          <div data-testid="stock-hammadde" className="mt-2 font-mono text-2xl font-semibold tabular text-amber-300">{hammadde.toLocaleString("tr-TR")}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Package className="h-4 w-4 text-sky-300" /> Mamül</div>
          <div data-testid="stock-mamul" className="mt-2 font-mono text-2xl font-semibold tabular text-sky-300">{mamul.toLocaleString("tr-TR")}</div>
        </div>
        <div className="rounded-xl border border-amber-500/40 bg-card p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><AlertTriangle className="h-4 w-4 text-amber-400" /> Kritik</div>
          <div data-testid="stock-critical" className="mt-2 font-mono text-2xl font-semibold tabular text-amber-400">{critical.toLocaleString("tr-TR")}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`stock-tab-${k}`} onClick={() => { setTab(k); setPage(0); }} className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
          <div className="mx-1 h-5 w-px bg-border" />
          <Button data-testid="stock-export-xlsx" size="sm" variant="outline" disabled={exporting === "xlsx"} onClick={() => doExport("xlsx")} className="h-8 gap-1.5 text-xs"><FileSpreadsheet className="h-3.5 w-3.5 text-emerald-400" /> Excel</Button>
          <Button data-testid="stock-export-pdf" size="sm" variant="outline" disabled={exporting === "pdf"} onClick={() => doExport("pdf")} className="h-8 gap-1.5 text-xs"><FileText className="h-3.5 w-3.5 text-rose-400" /> PDF</Button>
        </div>
        <div className="flex items-center gap-3">
          {tab !== "margin" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Eşik:</span>
              <Input data-testid="threshold-input" type="number" value={thInput} onChange={(e) => setThInput(e.target.value)} className="h-9 w-24" />
              <Button data-testid="threshold-save" size="sm" variant="outline" onClick={saveThreshold}><Save className="h-4 w-4" /></Button>
            </div>
          )}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="stock-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Ürün / kod ara..." className="pl-9" />
          </div>
        </div>
      </div>

      {tab === "margin" ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <SortHeader label="Ürün" sortId="name" sort={marginSort} />
                <SortHeader label="Ort. Alış" sortId="avg_buy" sort={marginSort} align="right" />
                <SortHeader label="Ort. Satış" sortId="avg_sell" sort={marginSort} align="right" />
                <SortHeader label="Birim Marj" sortId="margin" sort={marginSort} align="right" />
                <SortHeader label="Marj %" sortId="margin_pct" sort={marginSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {margins.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">Hem alınıp hem satılan eşleşen ürün bulunamadı</td></tr>}
              {sortedMargins.map((m, i) => (
                <tr key={i} data-testid="margin-row" className="border-b border-border/50 hover:bg-secondary/30">
                  <td className="px-5 py-3 font-medium">{m.name}</td>
                  <td className="px-5 py-3 text-right font-mono text-muted-foreground">{money(m.avg_buy)}</td>
                  <td className="px-5 py-3 text-right font-mono text-muted-foreground">{money(m.avg_sell)}</td>
                  <td className={`px-5 py-3 text-right font-mono font-semibold ${m.margin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(m.margin)}</td>
                  <td className={`px-5 py-3 text-right font-mono font-semibold ${(m.margin_pct || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{m.margin_pct != null ? `%${fmt(m.margin_pct)}` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <SortHeader label="Ürün" sortId="name" sort={stockSort} />
                  <SortHeader label="Kod" sortId="code" sort={stockSort} />
                  <SortHeader label="Kategori" sortId="category" sort={stockSort} />
                  <SortHeader label="Miktar" sortId="quantity" sort={stockSort} align="right" />
                  <SortHeader label="Satış Adedi" sortId="sell_qty" sort={stockSort} align="right" />
                  <SortHeader label="Ciro" sortId="sell_amount" sort={stockSort} align="right" />
                  <SortHeader label="Durum" sortId="status" sort={stockSort} align="center" />
                  <th className="px-5 py-3.5 text-right font-semibold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-muted-foreground">Kayıt bulunamadı</td></tr>}
                {sortedItems.map((s) => (
                  <tr key={s.id} data-testid="stock-row" className="border-b border-border/50 transition-colors hover:bg-secondary/30">
                    <td className="px-5 py-3 font-medium">
                      {nameEditId === s.id ? (
                        <div className="flex items-center gap-1">
                          <Input data-testid="stock-name-input" value={nameVal} autoFocus onChange={(e) => setNameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveName(s); if (e.key === "Escape") setNameEditId(null); }} className="h-7 w-64 text-xs" />
                          <button data-testid="stock-name-save" onClick={() => saveName(s)} className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setNameEditId(null)} className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <div className="group/name flex items-center gap-2">
                          {isCrit(s.quantity) && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-400">KRİTİK</span>}
                          <span>{s.name}</span>
                          <button data-testid="stock-name-edit" onClick={() => { setNameEditId(s.id); setNameVal(s.name || ""); }} title="Adı düzenle" className="opacity-0 transition-opacity group-hover/name:opacity-100"><Pencil className="h-3 w-3 text-muted-foreground" /></button>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {codeEditId === s.id ? (
                        <div className="flex items-center gap-1">
                          <Input data-testid="stock-code-input" value={codeVal} autoFocus onChange={(e) => setCodeVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveCode(s); if (e.key === "Escape") setCodeEditId(null); }} className="h-7 w-28 font-mono text-xs" />
                          <button data-testid="stock-code-save" onClick={() => saveCode(s)} className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setCodeEditId(null)} className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <button data-testid="stock-code-edit" onClick={() => { setCodeEditId(s.id); setCodeVal(s.code || ""); }} className="group/code flex items-center gap-1.5 rounded-md px-1.5 py-0.5 hover:bg-secondary" title="Kodu düzenle">
                          {s.code ? <span className="font-mono text-xs text-teal-300">{s.code}</span> : <span className="text-xs italic text-muted-foreground">kod ekle</span>}
                          <Pencil className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover/code:opacity-100" />
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <button data-testid="stock-category-toggle" onClick={() => toggleCategory(s)} title="Kategoriyi değiştir" className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition-colors ${s.category === "hammadde" ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" : "bg-sky-500/15 text-sky-300 hover:bg-sky-500/25"}`}>
                        {s.category === "hammadde" ? "Hammadde" : "Mamül"}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {editId === s.id ? (
                        <Input
                          data-testid="stock-qty-input"
                          type="number"
                          value={editVal}
                          autoFocus
                          onChange={(e) => setEditVal(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(s); if (e.key === "Escape") setEditId(null); }}
                          className="h-8 w-32 text-right font-mono ml-auto"
                        />
                      ) : (
                        <span className={`font-mono font-semibold tabular ${s.quantity < 0 ? "text-rose-400" : isCrit(s.quantity) ? "text-amber-400" : "text-emerald-400"}`}>{qty(s.quantity)}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span data-testid="stock-sell-qty" className="font-mono tabular text-sky-300">{s.sell_qty ? qty(s.sell_qty) : "-"}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="font-mono tabular text-muted-foreground">{s.sell_amount ? money(s.sell_amount) : "-"}</span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      {s.manual_override ? (
                        <span data-testid="stock-status-manual" className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold text-blue-400"><Pencil className="h-3 w-3" /> Düzeltildi</span>
                      ) : s.confirmed ? (
                        <span data-testid="stock-status-confirmed" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400"><BadgeCheck className="h-3 w-3" /> Onaylı</span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">Bekliyor</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {editId === s.id ? (
                          <>
                            <Button data-testid="stock-save-edit" size="sm" onClick={() => saveEdit(s)} className="h-8 bg-primary text-primary-foreground"><Check className="h-4 w-4" /></Button>
                            <Button data-testid="stock-cancel-edit" size="sm" variant="outline" onClick={() => setEditId(null)} className="h-8"><X className="h-4 w-4" /></Button>
                          </>
                        ) : (
                          <>
                            <Button data-testid="stock-confirm-btn" size="sm" variant="outline" disabled={s.confirmed} onClick={() => confirmRow(s)} className="h-8 gap-1 text-xs disabled:opacity-40"><Check className="h-3.5 w-3.5" /> Onayla</Button>
                            <Button data-testid="stock-edit-btn" size="sm" variant="ghost" onClick={() => startEdit(s)} className="h-8 gap-1 text-xs"><Pencil className="h-3.5 w-3.5" /> Düzelt</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {paged && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Sayfa {page + 1} / {totalPages}</div>
              <div className="flex items-center gap-2">
                <Button data-testid="stock-prev" variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><ChevronLeft className="h-4 w-4" /> Önceki</Button>
                <Button data-testid="stock-next" variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Sonraki <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
